import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, ShoppingCart, Trash2, Phone, LifeBuoy, Building2, UserSquare2, Loader2, Check } from "lucide-react";

// Common Twilio-supported countries for the picker
const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "GR", label: "Greece" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },
  { code: "NL", label: "Netherlands" },
  { code: "BE", label: "Belgium" },
  { code: "AT", label: "Austria" },
  { code: "CH", label: "Switzerland" },
  { code: "PT", label: "Portugal" },
  { code: "CY", label: "Cyprus" },
  { code: "SE", label: "Sweden" },
  { code: "DK", label: "Denmark" },
  { code: "NO", label: "Norway" },
  { code: "FI", label: "Finland" },
  { code: "PL", label: "Poland" },
];

export default function TwilioNumbersMarketplace({ L, companies }) {
  const [country, setCountry] = useState("US");
  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [available, setAvailable] = useState([]);
  const [owned, setOwned] = useState([]);
  const [balance, setBalance] = useState(null);
  const [reps, setReps] = useState([]);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState(null); // phone_number being purchased
  const [buyPick, setBuyPick] = useState(null); // {phone_number} → open assignment picker
  const [reassignPick, setReassignPick] = useState(null); // owned number being reassigned
  const [assignTo, setAssignTo] = useState("support");
  const [assignCompanyId, setAssignCompanyId] = useState("");
  const [assignUserId, setAssignUserId] = useState("");
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  const loadOwned = async () => {
    try {
      const { data } = await api.get("/twilio/admin/numbers/owned");
      setOwned(data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load owned numbers");
    }
  };
  const loadBalance = async () => {
    try {
      const { data } = await api.get("/twilio/admin/numbers/balance");
      setBalance(data);
    } catch { /* silent */ }
  };
  const loadReps = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setReps((data || []).filter((u) => u.role === "sales_rep" || u.role === "company_owner"));
    } catch { /* silent */ }
  };

  useEffect(() => { loadOwned(); loadBalance(); loadReps(); }, []);

  const search = async () => {
    setSearching(true);
    setErr("");
    try {
      const params = { country };
      if (areaCode) params.area_code = areaCode;
      if (contains) params.contains = contains;
      const { data } = await api.get("/twilio/admin/numbers/available", { params });
      setAvailable(data || []);
      if (!data.length) setNotice(L ? "Δεν βρέθηκαν διαθέσιμα νούμερα με αυτά τα κριτήρια." : "No numbers matched those criteria.");
      else setNotice("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Search failed");
    } finally { setSearching(false); }
  };

  const openBuyModal = (n) => {
    setBuyPick(n);
    setReassignPick(null);
    setAssignTo("support");
    setAssignCompanyId(companies?.[0]?.id || "");
    setAssignUserId(reps?.[0]?.id || "");
    setErr("");
  };

  const openReassignModal = (n) => {
    setReassignPick(n);
    setBuyPick(null);
    // Pre-select current assignment kind
    if (n.assignment?.kind === "support") setAssignTo("support");
    else if (n.assignment?.kind === "company") { setAssignTo("company"); setAssignCompanyId(n.assignment.id); }
    else if (n.assignment?.kind === "user") { setAssignTo("user"); setAssignUserId(n.assignment.id); }
    else setAssignTo("none");
    if (!assignCompanyId) setAssignCompanyId(companies?.[0]?.id || "");
    if (!assignUserId) setAssignUserId(reps?.[0]?.id || "");
    setErr("");
  };

  const buildAssignPayload = () => {
    if (assignTo === "support") return "support";
    if (assignTo === "company") return `company:${assignCompanyId}`;
    if (assignTo === "user") return `user:${assignUserId}`;
    return "none";
  };

  const confirmBuy = async () => {
    const assign = buildAssignPayload();
    setBuying(buyPick.phone_number);
    setErr("");
    try {
      await api.post("/twilio/admin/numbers/buy", {
        phone_number: buyPick.phone_number,
        assign_to: assign,
      });
      setNotice(L ? `Αγοράστηκε το ${buyPick.phone_number}` : `Purchased ${buyPick.phone_number}`);
      setBuyPick(null);
      await loadOwned();
      await loadBalance();
      setAvailable(available.filter((a) => a.phone_number !== buyPick.phone_number));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Purchase failed");
    } finally { setBuying(null); }
  };

  const confirmReassign = async () => {
    const assign = buildAssignPayload();
    setErr("");
    try {
      await api.post(`/twilio/admin/numbers/${reassignPick.sid}/assign`, { assign_to: assign });
      setNotice(L ? "Ανάθεση ενημερώθηκε" : "Assignment updated");
      setReassignPick(null);
      await loadOwned();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Assignment failed");
    }
  };

  const release = async (n) => {
    if (!window.confirm(L ? `Οριστική κατάργηση του νούμερου ${n.phone_number}; Δεν αντιστρέφεται.` : `Permanently release ${n.phone_number}? This cannot be undone.`)) return;
    setErr("");
    try {
      await api.delete(`/twilio/admin/numbers/${n.sid}`);
      await loadOwned();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Release failed");
    }
  };

  const capChip = (c, label) => (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${c ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
      {label}
    </span>
  );

  return (
    <div className="space-y-6" data-testid="admin-numbers-page">
      {/* Balance strip */}
      <div className="bg-white border border-slate-200 rounded-lg px-5 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Phone className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
              {L ? "Υπόλοιπο Twilio" : "Twilio balance"}
            </div>
            <div className="font-heading text-lg font-bold text-slate-900" data-testid="twilio-balance">
              {balance?.balance != null ? `${parseFloat(balance.balance).toFixed(2)} ${balance.currency || "USD"}` : "—"}
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-500 max-w-md">
          {L
            ? "Χρέωση: ~1€/μήνα ανά νούμερο + $0.0085–0.02 ανά λεπτό (ανάλογα με τη χώρα). Φόρτωση υπολοίπου από το Twilio Console."
            : "Cost: ~$1/month per number + $0.0085–0.02 per minute (varies by country). Top up from Twilio Console."}
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded">{err}</div>}
      {notice && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-2.5 rounded flex items-center gap-2"><Check className="w-4 h-4" /> {notice}</div>}

      {/* SEARCH */}
      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-500" />
          <h3 className="font-heading font-bold text-slate-900">{L ? "Αγορά νέου νούμερου" : "Buy a new number"}</h3>
        </header>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Χώρα" : "Country"}</label>
              <select
                data-testid="numbers-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
              >
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Κωδικός περιοχής" : "Area code"}</label>
              <input
                data-testid="numbers-area-code"
                type="text"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                placeholder={country === "US" ? "e.g. 415" : ""}
                className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Περιέχει" : "Contains"}</label>
              <input
                data-testid="numbers-contains"
                type="text"
                value={contains}
                onChange={(e) => setContains(e.target.value)}
                placeholder="1234 · WORD"
                className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                data-testid="numbers-search-btn"
                onClick={search}
                disabled={searching}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#0052CC] hover:bg-[#0043A8] text-white text-sm font-semibold rounded-md disabled:opacity-50"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {L ? "Αναζήτηση" : "Search"}
              </button>
            </div>
          </div>

          {available.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="numbers-available-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {[L ? "Αριθμός" : "Number", L ? "Περιοχή" : "Region", L ? "Δυνατότητες" : "Capabilities", ""].map((h) => (
                      <th key={h} className="py-2 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {available.map((n) => (
                    <tr key={n.phone_number} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono font-semibold text-slate-900">{n.phone_number}</td>
                      <td className="py-3 px-4 text-slate-700 text-xs">
                        {n.locality ? `${n.locality}, ` : ""}{n.region || n.iso_country}
                      </td>
                      <td className="py-3 px-4 space-x-1">
                        {capChip(n.capabilities.voice, "VOICE")}
                        {capChip(n.capabilities.sms, "SMS")}
                        {capChip(n.capabilities.mms, "MMS")}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          data-testid={`numbers-buy-${n.phone_number}`}
                          onClick={() => openBuyModal(n)}
                          disabled={buying === n.phone_number}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded shadow-sm disabled:opacity-50"
                        >
                          {buying === n.phone_number ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                          {L ? "Αγορά" : "Buy"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* OWNED */}
      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Phone className="w-4 h-4 text-slate-500" />
          <h3 className="font-heading font-bold text-slate-900">{L ? "Αγορασμένα νούμερα" : "Owned numbers"} ({owned.length})</h3>
        </header>
        {owned.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">{L ? "Δεν έχετε αγοράσει ακόμη νούμερα" : "No numbers purchased yet"}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="numbers-owned-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {[L ? "Αριθμός" : "Number", L ? "Ανάθεση" : "Assignment", L ? "Δημιουργήθηκε" : "Created", ""].map((h) => (
                    <th key={h} className="py-2 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {owned.map((n) => (
                  <tr key={n.sid} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono font-semibold text-slate-900">{n.phone_number}</td>
                    <td className="py-3 px-4">
                      {n.assignment ? (
                        n.assignment.kind === "support" ? (
                          <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-semibold px-2 py-1 rounded"><LifeBuoy className="w-3 h-3" /> {L ? "Υποστήριξη" : "Support"}</span>
                        ) : n.assignment.kind === "company" ? (
                          <span className="inline-flex items-center gap-1.5 bg-[#0052CC]/10 text-[#0052CC] text-xs font-semibold px-2 py-1 rounded"><Building2 className="w-3 h-3" /> {n.assignment.label}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-1 rounded"><UserSquare2 className="w-3 h-3" /> {n.assignment.label}</span>
                        )
                      ) : (
                        <span className="text-xs text-slate-400">{L ? "Χωρίς ανάθεση" : "Unassigned"}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500">{n.date_created?.slice(0, 10)}</td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <button
                        data-testid={`numbers-reassign-${n.sid}`}
                        onClick={() => openReassignModal(n)}
                        className="px-2.5 py-1 border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-semibold rounded"
                      >
                        {L ? "Ανάθεση" : "Reassign"}
                      </button>
                      <button
                        data-testid={`numbers-release-${n.sid}`}
                        onClick={() => release(n)}
                        className="p-1.5 text-slate-400 hover:text-red-600"
                        title={L ? "Κατάργηση" : "Release"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Assignment modal (used for both buy and reassign) */}
      {(buyPick || reassignPick) && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => { setBuyPick(null); setReassignPick(null); }}>
          <div className="bg-white rounded-xl max-w-md w-full border border-slate-200 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={buyPick ? "numbers-buy-modal" : "numbers-reassign-modal"}>
            <header className="px-6 py-4 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">
                {buyPick ? (L ? "Αγορά νούμερου" : "Buy number") : (L ? "Ανάθεση νούμερου" : "Reassign number")}
              </div>
              <div className="font-mono text-xl font-bold text-slate-900 mt-1">{(buyPick || reassignPick).phone_number}</div>
            </header>
            <div className="p-6 space-y-3 overflow-y-auto">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Ανάθεση σε" : "Assign to"}</label>

              {/* Support */}
              <label className="flex items-center gap-3 px-3 py-2.5 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 has-[:checked]:bg-[#0052CC]/5 has-[:checked]:border-[#0052CC]">
                <input type="radio" name="assign" value="support" checked={assignTo === "support"} onChange={(e) => setAssignTo(e.target.value)} data-testid="assign-support" />
                <LifeBuoy className="w-4 h-4 text-slate-500" />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{L ? "Ομάδα Υποστήριξης" : "Platform Support"}</div>
                  <div className="text-xs text-slate-500">{L ? "Κλήσεις της υποστήριξης προς χρήστες" : "Used for support calls to users"}</div>
                </div>
              </label>

              {/* Company */}
              <label className="flex items-start gap-3 px-3 py-2.5 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 has-[:checked]:bg-[#0052CC]/5 has-[:checked]:border-[#0052CC]">
                <input type="radio" name="assign" value="company" checked={assignTo === "company"} onChange={(e) => setAssignTo(e.target.value)} data-testid="assign-company-radio" className="mt-0.5" />
                <Building2 className="w-4 h-4 text-slate-500 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">{L ? "Ανάθεση σε εταιρεία" : "Assign to a company"}</div>
                  <div className="text-xs text-slate-500 mb-1.5">{L ? "Όλοι οι πωλητές της εταιρείας δέχονται τις κλήσεις" : "All reps of the company receive calls"}</div>
                  <select
                    data-testid="assign-company-select"
                    disabled={assignTo !== "company"}
                    value={assignCompanyId}
                    onChange={(e) => setAssignCompanyId(e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded disabled:opacity-50"
                  >
                    {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </label>

              {/* Individual user (sales rep / company owner) */}
              <label className="flex items-start gap-3 px-3 py-2.5 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 has-[:checked]:bg-[#0052CC]/5 has-[:checked]:border-[#0052CC]">
                <input type="radio" name="assign" value="user" checked={assignTo === "user"} onChange={(e) => setAssignTo(e.target.value)} data-testid="assign-user-radio" className="mt-0.5" />
                <UserSquare2 className="w-4 h-4 text-slate-500 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">{L ? "Ανάθεση σε συγκεκριμένο χρήστη" : "Assign to a specific user"}</div>
                  <div className="text-xs text-slate-500 mb-1.5">{L ? "Πωλητής ή ιδιοκτήτης — προσωπικό νούμερο" : "Sales rep or owner — personal number"}</div>
                  <select
                    data-testid="assign-user-select"
                    disabled={assignTo !== "user"}
                    value={assignUserId}
                    onChange={(e) => setAssignUserId(e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-slate-200 rounded disabled:opacity-50"
                  >
                    {reps.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} · {u.role === "sales_rep" ? (L ? "Πωλητής" : "Rep") : (L ? "Ιδιοκτήτης" : "Owner")} · {u.email}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              {/* Unassigned (only for reassign) */}
              {reassignPick && (
                <label className="flex items-center gap-3 px-3 py-2.5 border border-slate-200 rounded-md cursor-pointer hover:bg-slate-50 has-[:checked]:bg-slate-50">
                  <input type="radio" name="assign" value="none" checked={assignTo === "none"} onChange={(e) => setAssignTo(e.target.value)} data-testid="assign-none" />
                  <div className="w-4 h-4 rounded-full border-2 border-slate-300"></div>
                  <div className="text-sm font-semibold text-slate-500">{L ? "Καμία ανάθεση" : "Unassigned"}</div>
                </label>
              )}
            </div>

            <footer className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50/50">
              <button onClick={() => { setBuyPick(null); setReassignPick(null); }} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md" data-testid="numbers-modal-cancel">
                {L ? "Άκυρο" : "Cancel"}
              </button>
              <button
                data-testid={buyPick ? "numbers-buy-confirm" : "numbers-reassign-confirm"}
                onClick={buyPick ? confirmBuy : confirmReassign}
                disabled={buying || (assignTo === "user" && !assignUserId) || (assignTo === "company" && !assignCompanyId)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md shadow-sm disabled:opacity-50"
              >
                {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : buyPick ? <ShoppingCart className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                {buyPick ? (L ? "Επιβεβαίωση αγοράς" : "Confirm purchase") : (L ? "Αποθήκευση" : "Save")}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
